/**
 * The keybook's credential types and field matching — ONE definition, browser-safe (no imports), shared by the
 * credential card (`features/agent/ui/CredentialPrompt.tsx`: which saved keys fit a request, how a picked key fills
 * the form, how Update writes back), the server's per-turn resolver (`resolveByEntryId` — every turn, and a resumed
 * `cred_to_env` answer through `resolveQuestionCredentialEnvFor`) and the entries API's type validation (#1346).
 *
 * #1324 (owner, 2026-10-23): a requested field is matched to an entry's stored field by KIND, not only by name — a
 * secret field (`api_key`, `token`, `password`…) takes the entry's secret value; an identity field (`username`,
 * `email`, `account_id`…) takes its identity field. An exact name match always wins. A requested field's kind comes
 * from its NAME (`requestedFieldKind`) on both sides: the server only ever receives the field names of a request
 * (`envMapping`), so the card judging by the declared input type could offer a key the server then can't fill (D1).
 *
 * Never a guess (P1 — a value in the wrong variable is wrong data): a field falls back by kind only when that is
 * UNAMBIGUOUS — exactly one requested field of the kind is unmatched and exactly one stored field of the kind is free —
 * and a structured secret (a private key, a service-account JSON, a certificate, a connection string) matches only by
 * its exact name. Anything else stays unmatched, as an exact-name lookup always left it.
 */

/** Every credential type the keybook knows. The entries API refuses anything else (#1346). */
export const CREDENTIAL_TYPES = [
  'password', 'api_key', 'bearer', 'connection_string', 'ssh_key', 'service_account', 'oauth', 'certificate',
  'pat', 'github', 'custom',
] as const
export type KnownCredentialType = typeof CREDENTIAL_TYPES[number]

/** Exact membership — never an object lookup (`constructor` / `__proto__` are not types). */
export function isKnownCredentialType(value: unknown): value is KnownCredentialType {
  return typeof value === 'string' && (CREDENTIAL_TYPES as readonly string[]).includes(value)
}

export type FieldKind = 'secret' | 'identity' | 'other'
/** `exactOnly`: never a stand-in for a differently named field (a structured / encoded secret, a service-account or
 *  key-named identifier…) — but it still COUNTS toward ambiguity, so it can only ever leave a field unmatched. */
export interface StoredField { name: string; kind: FieldKind; exactOnly?: boolean }

/** The stored fields of each fixed-shape type, in form order. `github` / `oauth` / `custom` have no fixed shape. */
const FIXED_FIELDS: ReadonlyArray<readonly [KnownCredentialType, readonly StoredField[]]> = [
  ['password', [{ name: 'username', kind: 'identity' }, { name: 'password', kind: 'secret' }]],
  ['api_key', [{ name: 'key', kind: 'secret' }]],
  ['bearer', [{ name: 'token', kind: 'secret' }]],
  ['connection_string', [{ name: 'connection_string', kind: 'secret', exactOnly: true }]],
  ['ssh_key', [{ name: 'username', kind: 'identity' }, { name: 'private_key', kind: 'secret', exactOnly: true }]],
  ['service_account', [{ name: 'json', kind: 'secret', exactOnly: true }]],
  ['certificate', [{ name: 'certificate', kind: 'secret', exactOnly: true }, { name: 'private_key', kind: 'secret', exactOnly: true }]],
  ['pat', [{ name: 'pat', kind: 'secret' }]],
]

/** A fixed-shape type's stored fields; null for a type without one (or an unknown type). */
export function fixedCredentialFields(credentialType: string): readonly StoredField[] | null {
  return FIXED_FIELDS.find(([t]) => t === credentialType)?.[1] ?? null
}

/** Types whose secret is one bearer that fills EVERY requested field (unchanged by #1324). */
export function isBearerSplatType(credentialType: string): boolean {
  return credentialType === 'github' || credentialType === 'pat'
}

export type RequestedFieldKind = FieldKind

/** Head nouns that name a plain secret. */
const SECRET_HEADS = new Set([
  'password', 'passwd', 'pwd', 'pass', 'passphrase', 'pin', 'secret', 'apikey', 'token', 'bearer', 'pat',
  'credential', 'credentials',
])
/** Head nouns that name an identity. */
const IDENTITY_HEADS = new Set(['user', 'username', 'login', 'email', 'account', 'accountid', 'uid'])
/** Head nouns that name a STRUCTURED secret (exact-only): a certificate, a PEM, a DSN, a JSON blob, a private key. */
const STRUCTURED_HEADS = new Set(['certificate', 'cert', 'pem', 'dsn', 'json', 'privatekey'])
/** Modifiers that make a `…_key` a structured secret (`private_key`, `ssh_key`, `GCP_JSON_KEY`, `CERT_KEY`). */
const STRUCTURED_KEY_MODIFIERS = new Set(['private', 'ssh', 'pem', 'json', 'cert', 'certificate', 'dsn', 'connection'])
/** Words that make a `…_key` / `…_token` NOT the secret but a value shipped to browsers, anywhere in the name
 *  (`public_key`, `STRIPE_PUBLISHABLE_KEY`, Mapbox's `PUBLIC_TOKEN`). Only on a key / token head: a `SITE_PASSWORD` is
 *  a real password. */
const PUBLIC_KEY_MODIFIERS = new Set(['public', 'publishable'])   // not `pub`: in pub/sub SDKs it means publish
/** The same, but only as the word RIGHT BEFORE the head (Supabase's `ANON_KEY`, reCAPTCHA's `SITE_KEY`, a Maps
 *  `BROWSER_KEY`): elsewhere they name a product or a scope — `BROWSER_USE_API_KEY`, `SITE_API_KEY` and
 *  `SITE_ADMIN_TOKEN` are real secrets. */
const PUBLIC_HEAD_MODIFIERS = new Set(['anon', 'site', 'browser'])
/** Env-name prefixes a front-end build inlines into the page (Vite, Next.js, Expo, Create React App, SvelteKit, Nuxt,
 *  Gatsby): whatever its head, such a variable is public — a secret is never GUESSED into it (#1324 security review). */
// A deny-list — never complete (a custom Vite `envPrefix` names anything); it covers the common front-end builds.
const CLIENT_EXPOSED_PREFIXES = ['vite_', 'next_public_', 'expo_public_', 'react_app_', 'vue_app_', 'public_', 'nuxt_public_',
  'gatsby_', 'ng_app_', 'nx_public_', 'storybook_', 'sanity_studio_', 'redwood_env_', 'snowpack_public_', 'plasmo_public_']
/** Encoded forms, on the RAW words (before trailing digits drop): `TLS_CERT_B64`, `KEY_BASE64`. */
const ENCODED_WORDS = new Set(['b64', 'base64', 'encoded'])
/** Trailing modifiers that are not the head noun (`API_TOKEN_VALUE`, `TOKEN_V2`). */
const TRAILING_MODIFIERS = new Set(['value', 'v', 'raw', 'str'])   // not `base`: accountBase is a location (SECRET_KEY_BASE has its own rule)
/** Head nouns that take their kind from the word before (`user_name`, `EMAIL_ADDRESS`, `account_id`). */
const QUALIFIED_IDENTITY_HEADS = new Set(['name', 'address', 'id'])

/** A field name's class: its kind; `exactOnly` — never a stand-in (it still counts toward ambiguity, both sides);
 *  `optional` — a secret-shaped field a saved key is never REQUIRED to supply (a public / publishable key, Rails'
 *  `SECRET_KEY_BASE`). It counts toward ambiguity like any other. */
export interface FieldClass { kind: FieldKind; exactOnly: boolean; optional?: boolean }

/** A name's words: split on non-alphanumerics and lower→Upper camel steps (`privateKey` → private, key). */
function rawWords(name: string): string[] {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
}

/**
 * A field name's kind, decided by its HEAD NOUN — the last word, as in any compound name (`DEPLOY_TOKEN_USERNAME` is a
 * username, `POSTGRES_PASSWORD_FILE` a file path, `GCP_CREDENTIALS_JSON` a JSON, `EMAIL_HOST_PASSWORD` a password,
 * `access_key_id` an id). Trailing digits and modifiers don't count (`token1`, `API_TOKEN_VALUE`, `TOKEN_V2`),
 * nor an encoding word (`CONNECTION_STRING_B64`, `BASE64_ENCODED_KEY`).
 *  - a front-end build's variable (`VITE_…`, `NEXT_PUBLIC_…`, `EXPO_PUBLIC_…`, `REACT_APP_…`, `VUE_APP_…`, `PUBLIC_…`,
 *    `NUXT_PUBLIC_…`, `GATSBY_…`) is shipped to browsers: exact-only, and optional when secret-shaped;
 *  - a secret head → secret; a public `token` (`MAPBOX_PUBLIC_TOKEN`, an anon token) → exact-only and optional; a
 *    structured head, `connection_string` or a service account → a structured secret (exact-only);
 *  - `key` → a secret; exact-only when structured (private, ssh, pem, json, cert, dsn, connection), a service
 *    account's, named with an identity word (`PUSHOVER_USER_KEY` may be an identifier), or public (public /
 *    publishable anywhere, anon / site / browser right before the head — `STRIPE_PUBLISHABLE_KEY`,
 *    `SUPABASE_ANON_KEY`, `RECAPTCHA_SITE_KEY`: also optional, and it still counts toward ambiguity);
 *  - a key base (GitLab's `db_key_base`, Rails' `SECRET_KEY_BASE`) → a secret, exact-only; Rails' own is also optional;
 *  - a `…_PUB_KEY` / `pubKey` → exact-only (`pub` is public in JWT_PUB_KEY, publish in PubNub's PUB_KEY);
 *  - an identity head → identity (`name` / `address` / `id` after an identity word too: `user_name`, `EMAIL_ADDRESS`,
 *    `account_id`); exact-only for a service account's (`service_account_email` is not a login);
 *  - anything else (`host`, `path`, `file`, `url`, `region`, `secret_id`) → other: matched only by exact name.
 * Encoded forms (`…_b64`) are exact-only. `kind` (with `optional`) decides whether a saved key must supply the field
 * (`isRequiredSecretName`); `exactOnly` only ever BLOCKS a fallback — every field, exact-only or optional, counts
 * toward ambiguity in `matchCredentialFields`.
 */
export function classifyFieldName(name: string): FieldClass {
  const raw = rawWords(name)
  const encoded = raw.some((w) => ENCODED_WORDS.has(w))
  // the encoding word is no part of the name's meaning; trailing digits don't count (token1); trailing modifiers aren't
  // the head (API_TOKEN_VALUE → token)
  const words = raw.filter((w) => !ENCODED_WORDS.has(w)).map((w) => w.replace(/\d+$/, '')).filter(Boolean)
  while (words.length > 1 && TRAILING_MODIFIERS.has(words[words.length - 1]!)) words.pop()
  const head = words[words.length - 1] ?? ''
  const before = words[words.length - 2] ?? ''
  const identityWord = words.some((w) => IDENTITY_HEADS.has(w))
  const serviceAccount = words.includes('service') && words.includes('account')
  // public: a public word anywhere, or a head-only one right before the head. Accepted (the name can't tell): a
  // browser-public key named like a secret one — Algolia's SEARCH_API_KEY, Datadog RUM's CLIENT_TOKEN, a Mapbox
  // ACCESS_TOKEN, a Maps BROWSER_API_KEY — can still take a lone stored secret by kind (`search` / `client` / a
  // `browser` further back would break real secrets: BROWSER_USE_API_KEY); and a real secret named like a public one
  // (Webflow's short SITE_TOKEN) fills only by exact name, the safe direction.
  const publicWord = words.some((w) => PUBLIC_KEY_MODIFIERS.has(w)) || PUBLIC_HEAD_MODIFIERS.has(before)
  // a front-end build's variable (VITE_…, NEXT_PUBLIC_…) is public whatever its head: a secret-shaped one is
  // exact-only and optional, like a publishable key — a lone stored secret never fills it
  const lower = name.toLowerCase()
  if (CLIENT_EXPOSED_PREFIXES.some((p) => lower.startsWith(p))) {
    const c = classifyFieldName(name.slice(name.indexOf('_') + 1))
    return c.kind === 'secret' ? { kind: 'secret', exactOnly: true, optional: true } : { ...c, exactOnly: true }
  }
  if (SECRET_HEADS.has(head)) {
    // a public token (Mapbox's `PUBLIC_TOKEN`, an `ANON_TOKEN`): as a publishable key
    if (publicWord && (head === 'token' || head === 'apikey')) return { kind: 'secret', exactOnly: true, optional: true }
    return { kind: 'secret', exactOnly: encoded }
  }
  // a key BASE (GitLab's db_key_base / otp_key_base, Rails' SECRET_KEY_BASE): a structured app secret, exact-only;
  // Rails' own is also never required of a saved key (the app generates it)
  if (head === 'base' && before === 'key') {
    return { kind: 'secret', exactOnly: true, ...(words[words.length - 3] === 'secret' ? { optional: true } : {}) }
  }
  if (head === 'key') {
    // a public / publishable key: SECRET-shaped — counted toward ambiguity on both sides (a stored one beside the secret
    // key keeps the pair ambiguous; a requested one keeps a lone stored public key from filling the secret field) —
    // exact-only, and optional: never required of a saved key
    if (publicWord) return { kind: 'secret', exactOnly: true, optional: true }
    // a key named with an identity word may be an identifier (PUSHOVER_USER_KEY), a structured or a service account's
    // key is structured: never a stand-in
    // `pub` is ambiguous (public in JWT_PUB_KEY / pubKey, publish in PubNub's PUB_KEY): exact-only, never optional
    return { kind: 'secret', exactOnly: encoded || serviceAccount || identityWord || words.includes('pub') || words.some((w) => STRUCTURED_KEY_MODIFIERS.has(w)) }
  }
  if (STRUCTURED_HEADS.has(head) || (head === 'string' && before === 'connection')
    || (head === 'account' && before === 'service')) return { kind: 'secret', exactOnly: true }
  if (IDENTITY_HEADS.has(head) || (QUALIFIED_IDENTITY_HEADS.has(head) && IDENTITY_HEADS.has(before))) {
    return { kind: 'identity', exactOnly: encoded || serviceAccount }
  }
  return { kind: 'other', exactOnly: encoded }
}

/** Must a saved key supply this requested field? — a secret that isn't optional. The ONE answer for the fit check and
 *  the card's listing. */
export function isRequiredSecretName(name: string): boolean {
  const c = classifyFieldName(name)
  return c.kind === 'secret' && !c.optional
}

/** What an ENV VAR's name says about where its value goes — the exposure half of `classifyFieldName` only (Codex #1
 *  review): a front-end build's variable or a public key / token (shipped to browsers: exact-only and optional), an
 *  encoded form or a structured head (`…_PEM`, `…_JSON`, a connection string: another format, exact-only). NOT the
 *  word-anywhere rules (an identity, `private`, `pub`, a service account), which describe a field's own meaning — an
 *  env var `AZURE_STORAGE_ACCOUNT_KEY` or `MAILGUN_PRIVATE_KEY` is an ordinary place for an API key. The internal
 *  hidden git names (`__PAPAN_CRED_…`) are never exposed and say nothing. */
function envExposure(env: string): { exactOnly: boolean; optional: boolean } {
  if (env.startsWith('__PAPAN_CRED_')) return { exactOnly: false, optional: false }
  const lower = env.toLowerCase()
  if (CLIENT_EXPOSED_PREFIXES.some((p) => lower.startsWith(p))) return { exactOnly: true, optional: true }
  const e = classifyFieldName(env)
  if (e.kind === 'secret' && e.optional) return { exactOnly: true, optional: true }   // a public key / token
  const raw = rawWords(env)
  const words = raw.filter((w) => !ENCODED_WORDS.has(w)).map((w) => w.replace(/\d+$/, '')).filter(Boolean)
  while (words.length > 1 && TRAILING_MODIFIERS.has(words[words.length - 1]!)) words.pop()
  const head = words[words.length - 1] ?? ''
  const before = words[words.length - 2] ?? ''
  const structuredHead = STRUCTURED_HEADS.has(head) || (head === 'string' && before === 'connection')
  return { exactOnly: raw.some((w) => ENCODED_WORDS.has(w)) || structuredHead, optional: false }
}

/**
 * A REQUESTED field's class, judged by its name AND by the env var it lands in (#1324 owner review, Codex #1): the
 * model names both, and the env var is where the value goes — a field `api_key` bound to `VITE_SERVICE_KEY` is shipped
 * to browsers like a field named so. The field's own kind decides what it is; the env var's EXPOSURE (`envExposure`)
 * holds too. Only ever stricter.
 */
export function requestedFieldClass(name: string, env?: string): FieldClass {
  const c = classifyFieldName(name)
  if (!env || env === name) return c
  const x = envExposure(env)
  const optional = c.optional || (c.kind === 'secret' && x.optional)
  return { kind: c.kind, exactOnly: c.exactOnly || x.exactOnly, ...(optional ? { optional: true } : {}) }
}

/** The kind of a REQUESTED field (`classifyFieldName`) — a structured secret is still a `secret` (it is required). */
export function requestedFieldKind(name: string): RequestedFieldKind {
  return classifyFieldName(name).kind
}

/** A stored entry's field names that hold a value — the only candidates, for the card and the server alike. */
export function presentFieldNames(fields: Record<string, unknown>): string[] {
  return Object.keys(fields).filter((k) => typeof fields[k] === 'string' && (fields[k] as string).length > 0)
}

/**
 * An entry's stored fields with their secrecy. With `presentNames` (the decrypted entry): every present field — a
 * fixed-shape type's schema fields first, in form order, with the schema's secrecy, then any other present field
 * (the card's own "Save & use key" stores a request's names under `password`: `{ api_key }`), secret per `fieldsMeta`
 * else by name. Without (a listed entry, undecrypted): a fixed type's schema; null for any other shape (unknown).
 */
export function entryStoredFields(
  credentialType: string,
  presentNames?: readonly string[],
  fieldsMeta?: Record<string, { sensitive?: boolean } | undefined>,
): StoredField[] | null {
  const fixed = fixedCredentialFields(credentialType)
  if (!presentNames) return fixed ? [...fixed] : null
  const fromSchema = (fixed ?? []).filter((f) => presentNames.includes(f.name))
  const rest = presentNames
    .filter((name) => !fromSchema.some((f) => f.name === name))
    .map((name) => storedField(name, fieldsMeta?.[name]?.sensitive))
  return [...fromSchema, ...rest]
}

/** A non-schema stored field: marked sensitive → a secret; marked NOT sensitive → never a secret; else by name. A
 *  structured name stays exact-only either way (a sensitive `ssh_private_key` never fills an `api_key`). */
function storedField(name: string, sensitive: boolean | undefined): StoredField {
  const byName = classifyFieldName(name)
  const kind: FieldKind = sensitive === true ? 'secret' : sensitive === false && byName.kind === 'secret' ? 'other' : byName.kind
  return { name, kind, ...(byName.exactOnly ? { exactOnly: true } : {}) }
}

/**
 * Requested field name → the stored field that supplies it. Exact names first; then, per kind (secret, identity), a
 * fallback ONLY when unambiguous: exactly one requested field of that kind still unmatched and exactly one unused
 * stored field of that kind — counting exact-only fields on both sides — and neither of the two exact-only. An `other`
 * field matches only exactly. Unmatched requested fields are left out — never a guess that could put one secret in
 * another's variable.
 */
export function matchCredentialFields(
  requested: readonly string[],
  stored: readonly StoredField[],
  /** each requested field's env var (the request's `envMapping`) — judged with the field (`requestedFieldClass`) */
  envFor?: Readonly<Record<string, string>>,
): Record<string, string> {
  // no prototype: a requested name like `constructor` / `toString` must read as unmatched, never Object's member
  const out: Record<string, string> = Object.create(null)
  const used = new Set<string>()
  for (const r of requested) {
    if (stored.some((s) => s.name === r)) { out[r] = r; used.add(r) }
  }
  const classOf = new Map(requested.map((r) => [r, requestedFieldClass(r, envOf(envFor, r))] as const))
  for (const kind of ['secret', 'identity'] as const) {
    // every unmatched field of the kind COUNTS — an exact-only one included — so an exact-only field can never make a
    // fallback look unambiguous; it only blocks one, and is never the one picked
    const wanting = requested.filter((r) => !Object.prototype.hasOwnProperty.call(out, r) && classOf.get(r)!.kind === kind)
    const free = stored.filter((s) => !used.has(s.name) && s.kind === kind)
    if (wanting.length === 1 && free.length === 1 && !classOf.get(wanting[0]!)!.exactOnly && !free[0]!.exactOnly) {
      out[wanting[0]!] = free[0]!.name
      used.add(free[0]!.name)
    }
  }
  return out
}

/**
 * Does a saved entry fit this request? (The card's "compatible" — a bearer type is judged by provider elsewhere.)
 * Matched over EVERY requested name, exactly as the server matches `envMapping` (a field's presence changes what is
 * unambiguous); yes when at least one requested field is supplied and no requested SECRET field is left without a
 * value — except the `optional` names (a `prompt` field: typed on the card each time, not required of a saved key).
 * An unknown shape never fits.
 */
export function entryFitsRequest(
  requested: readonly string[],
  stored: readonly StoredField[] | null,
  optional: ReadonlySet<string> = new Set(),
  envFor?: Readonly<Record<string, string>>,
): boolean {
  if (!stored) return false
  const m = matchCredentialFields(requested, stored, envFor)
  if (Object.keys(m).length === 0) return false
  return requested.every((r) => {
    if (Object.prototype.hasOwnProperty.call(m, r) || optional.has(r)) return true
    const c = requestedFieldClass(r, envOf(envFor, r))
    return !(c.kind === 'secret' && !c.optional)
  })
}

/** A requested field's env var, looked up as an OWN key only (a field named `constructor` has none). */
function envOf(envFor: Readonly<Record<string, string>> | undefined, r: string): string | undefined {
  return envFor && Object.prototype.hasOwnProperty.call(envFor, r) ? envFor[r] : undefined
}

/**
 * The card's form values for a picked entry: its stored fields, plus each requested field filled from the stored
 * field the matcher picks (a bearer type's one secret fills every requested field, as before #1324).
 */
/** Set a field as an OWN property — `out[k] = v` with k = `__proto__` would hit Object's prototype setter and drop the
 *  field (a key holding one, stored before or by the API, must keep it through a write-back — #1324 owner review). */
function setOwn(out: Record<string, string>, k: string, v: string): void {
  Object.defineProperty(out, k, { value: v, enumerable: true, writable: true, configurable: true })
}

export function formValuesForEntry(
  requested: readonly string[],
  credentialType: string,
  fields: Record<string, string>,
  fieldsMeta?: Record<string, { sensitive?: boolean } | undefined>,
  envFor?: Readonly<Record<string, string>>,
): Record<string, string> {
  const out: Record<string, string> = { ...fields }
  if (isBearerSplatType(credentialType)) {
    const bearer = credentialType === 'github' ? fields.access_token : fields.pat
    if (bearer) for (const r of requested) if (!Object.prototype.hasOwnProperty.call(out, r) || !out[r]) setOwn(out, r, bearer)
    return out
  }
  const m = matchCredentialFields(requested, entryStoredFields(credentialType, presentFieldNames(fields), fieldsMeta) ?? [], envFor)
  for (const r of requested) if (!Object.prototype.hasOwnProperty.call(out, r) || !out[r]) { if (m[r] !== undefined) setOwn(out, r, fields[m[r]]!) }
  return out
}

/**
 * The inverse, for the card's Update: the form back onto the entry's OWN field names — a value typed into a requested
 * field the matcher filled from stored field `key` is written to `key` (never a second `api_key` copy beside a stale
 * `key`). Matched on `loaded` — the entry EXACTLY AS READ, the presence the fill matched on — never the edited form:
 * clearing a field or typing into an empty one must not re-map another. Every stored field is kept (the PATCH
 * replaces the payload); a stored field that was only another's alias keeps what it held; a requested field the
 * entry never had becomes a new field.
 * A bearer type is returned as `{...loaded, ...form}`.
 */
export function storedValuesFromForm(
  requested: readonly string[],
  credentialType: string,
  loaded: Record<string, string>,
  form: Record<string, string>,
  fieldsMeta?: Record<string, { sensitive?: boolean } | undefined>,
  envFor?: Readonly<Record<string, string>>,
): Record<string, string> {
  const values: Record<string, string> = { ...loaded, ...form }
  if (isBearerSplatType(credentialType)) return values
  const storedNames = Object.keys(loaded)
  const m = matchCredentialFields(requested, entryStoredFields(credentialType, presentFieldNames(loaded), fieldsMeta) ?? [], envFor)
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(values)) {
    const aliasOf = m[k] !== undefined && m[k] !== k ? m[k] : undefined
    if (aliasOf === undefined && !Object.prototype.hasOwnProperty.call(out, k)) setOwn(out, k, v)
    // a STORED field that was only the alias of another (it held nothing at load) keeps what it held — its form
    // value belongs to the field it stood for
    else if (aliasOf !== undefined && storedNames.includes(k)) setOwn(out, k, loaded[k]!)
  }
  for (const [k, v] of Object.entries(values)) {
    const aliasOf = m[k] !== undefined && m[k] !== k ? m[k] : undefined
    if (aliasOf !== undefined) setOwn(out, aliasOf, v)   // the form's field is what the person sees and edits: it wins
  }
  return out
}

/**
 * #1441 (owner, 2026-10-28) — the EXACT field map a credential card showed: requested field → the stored field that
 * holds the value it showed, in the entry as it stands once the card's own write (if any) has landed (`stored`). Each
 * binding and each `cred_to_env` answer carries it, and the server resolves through it — never re-matching, so a key
 * whose fields change later (the card's own Update, or another person editing a SHARED key) never routes a value the
 * card didn't show into a variable. Only a field the card showed WITH a value is mapped, and only to a stored field
 * holding exactly that value: a field shown empty stays unmapped (never the key's other secret). Names only, never a
 * value. A bearer type (github / pat) has no map — its one secret fills every field. A `prompt` field is never stored,
 * so never mapped (pass it in `exclude`).
 */
export function cardFieldMap(
  requested: readonly string[],
  credentialType: string,
  stored: Record<string, string>,
  shown: Record<string, string>,
  fieldsMeta?: Record<string, { sensitive?: boolean } | undefined>,
  envFor?: Readonly<Record<string, string>>,
  exclude: ReadonlySet<string> = new Set(),
): Record<string, string> | undefined {
  if (isBearerSplatType(credentialType)) return undefined
  const m = matchCredentialFields(requested, entryStoredFields(credentialType, presentFieldNames(stored), fieldsMeta) ?? [], envFor)
  const out: Record<string, string> = Object.create(null)
  for (const r of requested) {
    if (exclude.has(r) || !Object.prototype.hasOwnProperty.call(m, r)) continue
    const v = Object.prototype.hasOwnProperty.call(shown, r) ? shown[r] : undefined
    const from = m[r]!
    const held = Object.prototype.hasOwnProperty.call(stored, from) ? stored[from] : undefined
    if (typeof v === 'string' && v.length > 0 && held === v) setOwn(out, r, from)
  }
  return { ...out }
}

/** The most fields one map may name (a request is a handful of fields; anything bigger is not a card's). */
export const FIELD_MAP_MAX_FIELDS = 64
/** The longest field name a map may carry. */
export const FIELD_MAP_MAX_NAME = 256

/**
 * #1441 — a field map as it ARRIVES (a request body, a stored row, a mailbox value): a plain object of
 * requested-field-name → stored-field-name strings, each requested name one the binding's `envMapping` names. Anything
 * else is no map (`undefined`), so the caller keeps today's rule — never a half-trusted map. Names only.
 *
 * By design it isn't checked against what a card showed: a request body is the person's own, on a key they may use
 * (`resolveByEntryId` checks that). A hand-written client could already name any stored field through `envMapping`, so
 * the map protects a person from a key CHANGING under their binding, not from choosing a field of their own key.
 */
export function parseFieldMap(raw: unknown, envMapping?: Readonly<Record<string, string>>): Record<string, string> | undefined {
  let v = raw
  if (typeof v === 'string') { try { v = JSON.parse(v) } catch { return undefined } }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined
  const entries = Object.entries(v as Record<string, unknown>)
  if (entries.length > FIELD_MAP_MAX_FIELDS) return undefined
  const out: Record<string, string> = Object.create(null)
  for (const [k, s] of entries) {
    if (typeof s !== 'string' || !s || s.length > FIELD_MAP_MAX_NAME || !k || k.length > FIELD_MAP_MAX_NAME) return undefined
    if (envMapping && !Object.prototype.hasOwnProperty.call(envMapping, k)) return undefined
    setOwn(out, k, s)
  }
  return { ...out }
}

/**
 * #1441 — resolve a request's fields THROUGH its card's field map: each requested field takes the stored field the map
 * names, when that field still holds a value; a field the map doesn't name, or whose stored field is gone or empty,
 * stays unmatched. Never re-matched. (Requested field → stored field name, as `matchCredentialFields` returns it.)
 */
export function matchThroughFieldMap(
  requested: readonly string[],
  fields: Record<string, unknown>,
  fieldMap: Readonly<Record<string, string>>,
): Record<string, string> {
  const out: Record<string, string> = Object.create(null)
  for (const r of requested) {
    if (!Object.prototype.hasOwnProperty.call(fieldMap, r)) continue
    const from = fieldMap[r]!
    const v = Object.prototype.hasOwnProperty.call(fields, from) ? fields[from] : undefined
    if (typeof v === 'string' && v.length > 0) out[r] = from
  }
  return out
}
